<template>
  <div class="container-fluid">
    <!-- Header -->
    <b-card class="mb-2">
      <b-card-header class="p-1">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h4 class="mb-0">Create Broker Job</h4>
            <small class="text-muted">Post a new job to the broker network</small>
          </div>
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="goBack"
          >
            <feather-icon icon="ArrowLeftIcon" class="mr-50" />
            Back to List
          </b-button>
        </div>
      </b-card-header>
    </b-card>

    <!-- Form -->
    <b-card>
      <b-overlay
        :show="isLoading"
        rounded="sm"
        opacity="0.6"
      >
        <validation-observer ref="formValidator">
          <b-form @submit.prevent="onSubmit">
            <!-- Basic Information Section -->
            <div class="mb-3">
              <h5 class="mb-1">Basic Information</h5>
              <hr>
            </div>

            <b-row>
              <!-- Position (Required) -->
              <b-col md="12">
                <validation-provider
                  #default="{ errors }"
                  name="Position"
                  rules="required"
                >
                  <b-form-group
                    label="Job Position"
                    label-for="position"
                  >
                    <template #label>
                      Job Position <span class="text-danger">*</span>
                    </template>
                    <b-form-input
                      id="position"
                      v-model="formData.position"
                      placeholder="e.g. Senior Software Engineer"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Description (Required) -->
              <b-col md="12">
                <validation-provider
                  #default="{ errors }"
                  name="Description"
                  rules="required"
                >
                  <b-form-group
                    label="Job Description"
                    label-for="description"
                  >
                    <template #label>
                      Job Description <span class="text-danger">*</span>
                    </template>
                    <b-form-textarea
                      id="description"
                      v-model="formData.description"
                      placeholder="Describe the role, company, and opportunity..."
                      rows="5"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <!-- Requirements -->
              <b-col md="6">
                <b-form-group
                  label="Requirements"
                  label-for="requirements"
                >
                  <b-form-textarea
                    id="requirements"
                    v-model="formData.requirements"
                    placeholder="List required qualifications, experience, etc."
                    rows="4"
                  />
                </b-form-group>
              </b-col>

              <!-- Responsibilities -->
              <b-col md="6">
                <b-form-group
                  label="Responsibilities"
                  label-for="responsibilities"
                >
                  <b-form-textarea
                    id="responsibilities"
                    v-model="formData.responsibilities"
                    placeholder="Key responsibilities and duties..."
                    rows="4"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Employment Details Section -->
            <div class="mb-3 mt-2">
              <h5 class="mb-1">Employment Details</h5>
              <hr>
            </div>

            <b-row>
              <!-- Employment Type -->
              <b-col md="6">
                <b-form-group
                  label="Employment Type"
                  label-for="employment_type"
                >
                  <v-select
                    id="employment_type"
                    v-model="formData.employment_type"
                    :options="employmentTypeOptions"
                    multiple
                    placeholder="Select employment type(s)"
                  />
                </b-form-group>
              </b-col>

              <!-- Industry -->
              <b-col md="6">
                <b-form-group
                  label="Industry"
                  label-for="industry"
                >
                  <v-select
                    id="industry"
                    v-model="formData.industry"
                    :options="industryOptions"
                    multiple
                    placeholder="Select industry"
                  />
                </b-form-group>
              </b-col>

              <!-- Required Skills -->
              <b-col md="12">
                <b-form-group
                  label="Required Skills"
                  label-for="required_skills"
                >
                  <v-select
                    id="required_skills"
                    v-model="formData.required_skills"
                    :options="skillsOptions"
                    multiple
                    taggable
                    placeholder="Type to add skills"
                  />
                  <small class="text-muted">Type and press enter to add custom skills</small>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Salary & Experience Section -->
            <div class="mb-3 mt-2">
              <h5 class="mb-1">Salary & Experience</h5>
              <hr>
            </div>

            <b-row>
              <!-- Min Salary -->
              <b-col md="3">
                <b-form-group
                  label="Minimum Salary"
                  label-for="min_salary"
                >
                  <b-form-input
                    id="min_salary"
                    v-model.number="formData.min_salary"
                    type="number"
                    placeholder="e.g. 5000"
                  />
                </b-form-group>
              </b-col>

              <!-- Max Salary -->
              <b-col md="3">
                <b-form-group
                  label="Maximum Salary"
                  label-for="max_salary"
                >
                  <b-form-input
                    id="max_salary"
                    v-model.number="formData.max_salary"
                    type="number"
                    placeholder="e.g. 8000"
                  />
                </b-form-group>
              </b-col>

              <!-- Salary Currency -->
              <b-col md="3">
                <b-form-group
                  label="Currency"
                  label-for="salary_currency"
                >
                  <v-select
                    id="salary_currency"
                    v-model="formData.salary_currency"
                    :options="currencyOptions"
                    placeholder="Select currency"
                  />
                </b-form-group>
              </b-col>

              <!-- Salary Cycle -->
              <b-col md="3">
                <b-form-group
                  label="Salary Cycle"
                  label-for="salary_cycle"
                >
                  <v-select
                    id="salary_cycle"
                    v-model="formData.salary_cycle"
                    :options="salaryCycleOptions"
                    placeholder="Select cycle"
                  />
                </b-form-group>
              </b-col>

              <!-- Min Years of Experience -->
              <b-col md="6">
                <b-form-group
                  label="Minimum Years of Experience"
                  label-for="min_yoe"
                >
                  <b-form-input
                    id="min_yoe"
                    v-model.number="formData.min_yoe"
                    type="number"
                    placeholder="e.g. 3"
                  />
                </b-form-group>
              </b-col>

              <!-- Max Years of Experience -->
              <b-col md="6">
                <b-form-group
                  label="Maximum Years of Experience"
                  label-for="max_yoe"
                >
                  <b-form-input
                    id="max_yoe"
                    v-model.number="formData.max_yoe"
                    type="number"
                    placeholder="e.g. 10"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Location Section -->
            <div class="mb-3 mt-2">
              <h5 class="mb-1">Location</h5>
              <hr>
            </div>

            <b-row>
              <!-- State -->
              <b-col md="6">
                <b-form-group
                  label="State/Region"
                  label-for="working_location_state"
                >
                  <b-form-input
                    id="working_location_state"
                    v-model="formData.working_location_state"
                    placeholder="e.g. Singapore"
                  />
                </b-form-group>
              </b-col>

              <!-- Country -->
              <b-col md="6">
                <b-form-group
                  label="Country"
                  label-for="working_location_country"
                >
                  <b-form-input
                    id="working_location_country"
                    v-model="formData.working_location_country"
                    placeholder="e.g. Singapore"
                  />
                </b-form-group>
              </b-col>

              <!-- Postal Code -->
              <b-col md="6">
                <b-form-group
                  label="Postal Code"
                  label-for="working_location_postal_code"
                >
                  <b-form-input
                    id="working_location_postal_code"
                    v-model="formData.working_location_postal_code"
                    placeholder="e.g. 123456"
                  />
                </b-form-group>
              </b-col>

              <!-- Full Address -->
              <b-col md="6">
                <b-form-group
                  label="Full Address"
                  label-for="working_location_address"
                >
                  <b-form-input
                    id="working_location_address"
                    v-model="formData.working_location_address"
                    placeholder="e.g. 123 Main Street"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Broker Details Section -->
            <div class="mb-3 mt-2">
              <h5 class="mb-1">Broker Details</h5>
              <hr>
            </div>

            <b-row>
              <!-- Expiration Date -->
              <b-col md="6">
                <b-form-group
                  label="Expiration Date"
                  label-for="expires_on"
                >
                  <b-form-input
                    id="expires_on"
                    v-model="formData.expires_on"
                    type="date"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Contact Information Section -->
            <div class="mb-3 mt-2">
              <h5 class="mb-1">Contact Information</h5>
              <hr>
            </div>

            <b-row>
              <!-- Contact Person -->
              <b-col md="4">
                <b-form-group
                  label="Contact Person"
                  label-for="contact_person"
                >
                  <b-form-input
                    id="contact_person"
                    v-model="formData.contact_person"
                    placeholder="Your name"
                  />
                  <small class="text-muted">Defaults to your profile name</small>
                </b-form-group>
              </b-col>

              <!-- Contact Email -->
              <b-col md="4">
                <b-form-group
                  label="Contact Email"
                  label-for="contact_email"
                >
                  <b-form-input
                    id="contact_email"
                    v-model="formData.contact_email"
                    type="email"
                    placeholder="your@email.com"
                  />
                  <small class="text-muted">Defaults to your profile email</small>
                </b-form-group>
              </b-col>

              <!-- Contact Phone -->
              <b-col md="4">
                <b-form-group
                  label="Contact Phone"
                  label-for="contact_phone"
                >
                  <b-form-input
                    id="contact_phone"
                    v-model="formData.contact_phone"
                    placeholder="+65 1234 5678"
                  />
                  <small class="text-muted">Defaults to your profile phone</small>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Action Buttons -->
            <div class="mt-3 d-flex justify-content-end">
              <b-button
                variant="outline-secondary"
                class="mr-2"
                @click="goBack"
              >
                Cancel
              </b-button>
              <b-button
                type="submit"
                variant="primary"
                :disabled="isLoading"
              >
                <b-spinner
                  v-if="isLoading"
                  small
                  class="mr-50"
                />
                Create Broker Job
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BRow,
  BCol,
  BOverlay,
  BSpinner,
  BInputGroup,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import vSelect from 'vue-select'

export default {
  components: {
    BCard,
    BCardHeader,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BRow,
    BCol,
    BOverlay,
    BSpinner,
    BInputGroup,
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },
  data() {
    return {
      isLoading: false,
      formData: {
        position: '',
        description: '',
        requirements: '',
        responsibilities: '',
        employment_type: [],
        industry: [],
        required_skills: [],
        min_salary: null,
        max_salary: null,
        salary_currency: 'SGD',
        salary_cycle: 'Monthly',
        min_yoe: null,
        max_yoe: null,
        working_location_state: '',
        working_location_country: '',
        working_location_postal_code: '',
        working_location_address: '',
        commission_percentage: 15.0,
        expires_on: '',
        contact_person: '',
        contact_email: '',
        contact_phone: '',
      },
      // Options
      employmentTypeOptions: [
        'Full-time',
        'Part-time',
        'Contract',
        'Temporary',
        'Internship',
        'Freelance',
      ],
      industryOptions: [
        'Technology',
        'Finance',
        'Healthcare',
        'Education',
        'Manufacturing',
        'Retail',
        'Hospitality',
        'Construction',
        'Marketing',
        'Human Resources',
      ],
      skillsOptions: [
        'JavaScript',
        'Python',
        'Java',
        'React',
        'Vue.js',
        'Node.js',
        'SQL',
        'Project Management',
        'Sales',
        'Customer Service',
      ],
      currencyOptions: ['SGD', 'USD', 'EUR', 'GBP', 'MYR', 'CNY'],
      salaryCycleOptions: ['Monthly', 'Annually', 'Hourly'],
      // Validation
      required,
    }
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'broker-list' })
    },
    async onSubmit() {
      const isValid = await this.$refs.formValidator.validate()
      if (!isValid) {
        this.$toastWarning('Please fill in all required fields', 'Validation Error')
        return
      }

      this.isLoading = true

      try {
        const response = await this.$http.post('/api/broker/jobs', this.formData)

        if (response.data.success) {
          this.$toastSuccess('Broker job created successfully!', 'Success')
          // Redirect to the created job details page
          this.$router.push({
            name: 'broker-details',
            params: { job_id: response.data.data.job_id },
          })
        } else {
          this.$toastDanger(response.data.message || 'Failed to create job', 'Error')
        }
      } catch (error) {
        console.error('Error creating broker job:', error)
        this.$toastDanger(
          error.response?.data?.detail || 'An error occurred while creating the job',
          'Error'
        )
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .vs__dropdown-toggle {
  border-color: #d8d6de;
}
</style>
